import type { CacheEntry, LookupResult } from './types.js'

/**
 * In-memory cache with TTL for namespace lookups
 */
export class NamespaceCache {
  ttl: number
  cache: Map<string, CacheEntry>

  constructor(ttl: number) {
    this.ttl = ttl
    this.cache = new Map()
  }

  get(name: string): CacheEntry | null {
    const entry = this.cache.get(name)
    if (!entry) return null

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(name)
      return null
    }

    return entry
  }

  set(name: string, data: LookupResult): void {
    const now = Date.now()
    this.cache.set(name, {
      ...data,
      timestamp: now,
      expiresAt: now + this.ttl,
    })
  }

  getMany(names: string[]): { cached: Map<string, CacheEntry>; missing: string[] } {
    const cached = new Map<string, CacheEntry>()
    const missing: string[] = []

    for (const name of names) {
      const entry = this.get(name)
      if (entry) {
        cached.set(name, entry)
      } else {
        missing.push(name)
      }
    }

    return { cached, missing }
  }

  setMany(results: LookupResult[]): void {
    for (const result of results) {
      this.set(result.name, result)
    }
  }

  invalidate(name: string): void {
    this.cache.delete(name)
  }

  invalidateAll(): void {
    this.cache.clear()
  }
}
